/*******************************************************************************
 *
 * This file is part of the LGS1920/studio project.
 *
 * File: MapPOIMonitor.jsx
 *
 * Author : LGS1920 Team
 * email: studio@lgs1920.fr
 *
 * Created on: 2026-04-26
 * Last modified: 2026-04-26
 *
 *
 * Copyright © 2026 LGS1920
 ******************************************************************************/

import { MapPOIContent } from '@Components/MainUI/MapPOI/MapPOIContent'
import { POIS_EDITOR_DRAWER } from '@Core/constants'
import { useSnapshot }        from 'valtio'
import { useEffect, useRef }  from 'react'
import { updatedDiff }        from 'deep-object-diff'

/**
 * Checks if an object is empty by verifying it has no own properties
 * @param {Object} obj - Object to check
 * @returns {boolean} True if the object has no own properties, false otherwise
 */
const isEmpty = obj => Object.keys(obj).length === 0

/**
 * React component that watches a Valtio proxy Map for MapPOI additions, removals,
 * or attribute changes, triggering draw, remove, or redraw actions
 * @returns {null} Returns null as the component has no UI
 */
export const MapPOIMonitor = () => {
    // Get reactive snapshot of the POI list from Valtio store
    const $pois = lgs.stores.main.components.pois
    const pois = useSnapshot($pois)
    const currentList = useSnapshot($pois.list)
    // Store previous POI list state for comparison
    const _previousList = useRef(new Map())
    let current = null
    const _menuMonitor = useRef(null)

    /**
     * Get POI by id and set current poi.
     *
     * @param id
     * @return {*}
     */
    const getPOI = (id) => {
        const current = currentList.get(id)
        $pois.current = id
        return current
    }

    /**
     * Toggle editor Handler
     *
     * For the same poi, it toggles the editor pane.
     *
     * When the editor is open on a poi  and the user double click on another one,
     * the editor stays open but focuses on th new poi data.
     *
     * @param event
     * @param entity
     */
    const handleEditor = (event, entity) => {
        if (__.ui.drawerManager.drawers.open && entity !== current) {
            __.ui.drawerManager.close()
        }
        getPOI(entity)
        __.ui.drawerManager.toggle(POIS_EDITOR_DRAWER, {
            action: 'edit-current',
            entity: entity,
            tab:    'pois',
        })
        current = entity
    }

    /**
     * Show context menu for the selected poi.
     *
     * @param event
     * @param entity
     */
    const handleContextMenu = (event, entity) => {
        const poi = getPOI(entity)
        const isCameraBusy = __.ui.cameraManager.isRotating() || lgs.stores.ui.mainUI.panorama.active
        if (poi && !isCameraBusy
            || (isCameraBusy
                &&
                (pois.current === false || pois.current.id === poi.id)
            )) {
            __.app.hooksContextMenu(event)
            $pois.context.visible = true
        }
    }

    /**
     * Adds event listeners for a MapPOI
     * @param {MapPOI} poi - MapPOI instance to add listeners for
     */
    const addPOIEventListeners = poi => {
        // Toggles POI size on click
        __.canvasEvents.onClick(poi.toggleExpand, {entity: poi.id})
        __.canvasEvents.onTap(poi.toggleExpand, {entity: poi.id})

        // Open editor on Double Click/double tap
        __.canvasEvents.onDoubleClick(handleEditor, {entity: poi.id, preventLowerPriority: true})
        __.canvasEvents.onDoubleTap(handleEditor, {entity: poi.id, preventLowerPriority: true})

        // Open contextual menu on Right Click/long tap
        __.canvasEvents.onRightClick(handleContextMenu, {entity: poi.id, preventLowerPriority: true})
        __.canvasEvents.onLongTap(handleContextMenu, {entity: poi.id, preventLowerPriority: true})

        // __.canvasEvents.onKeyDown(
        //     (event, entityId, options, userData) => {
        //         if (event.altKey) { // Exclure Shift, même si Ctrl+Alt sont requis
        //             console.log('Alt+S (no Shift):', {entityId, userData})
        //         }
        //     },
        //     {modifiers: ['alt'], keys: ['s'], entity: poi.id},
        //     {action: 'save'},
        // )
    }

    /**
     * Removes event listeners for a MapPOI
     * @param {MapPOI} poi - MapPOI instance to remove listeners for
     */
    const removePOIEventListeners = poi => {
        __.canvasEvents.removeAllListenersByEntity(poi.id)
    }

    return (
        <div id="map-poi-list">
            {Array.from(currentList, ([id, poi]) => (
                <MapPOIContent key={id} poi={poi.id}/>
            ))}
        </div>
    )
}
